// AmsCase.tsx — the single case record.
//
// This is the screen the whole plan exists to produce. Today the history lives "sporadically in
// the comment field in TCMS"; here every notice, message, call, visit, letter and payment is one
// ordered list against one account.
//
// Two things it must always show, and a designer should not remove:
//   • THE LIVE POSITION FROM TCMS, beside the cached one, with its age. If they differ, that is
//     information, not a rendering glitch.
//   • THE SUPPRESSION IN FORCE, with who applied it and until when — the record that answers
//     "why did this resident not receive a letter", which an auditor and a resident both ask.
//   • THE HARDSHIP RULE IN FULL when one applies — what the system does, what is offered, what
//     evidence is held and when it expires. An officer acting on a case is entitled to see the
//     policy that governs it without leaving the page.
import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useAuth } from '../../auth/AuthContext'
import { can } from '../../lib/capabilities'
import RecordContact from './RecordContact'
import Touchpoints from './Touchpoints'
import { api, ApiError, type HardshipRule } from '../../lib/api'
import { ageOf, CATEGORIES, dateOf, DRIVERS, money, ROLE_LABELS } from '../../lib/format'

type Data = Awaited<ReturnType<typeof api.ams.case>>

const TIER_LABEL: Record<string, string> = {
  plan_conditional: 'Plan-conditional', deferral: 'Deferral', hard_stop: 'Hard stop',
}

export default function AmsCase() {
  const { id = '' } = useParams()
  const { me, identity } = useAuth()
  const org = me?.org?.id
  const [d, setD] = useState<Data | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<string | null>(null)
  const [cat, setCat] = useState('')
  const [driver, setDriver] = useState('')

  const load = () => {
    if (!org) return
    api.ams.case(org, id)
      .then(r => { setD(r); setCat(r.case.category ?? ''); setDriver(r.case.hardshipCode ?? '') })
      .catch((e: unknown) => setError(e instanceof ApiError ? e.detail : String(e)))
  }
  useEffect(load, [org, id])

  async function save() {
    if (!org) return
    setBusy(true); setMsg(null)
    try {
      const r = await api.ams.setCategory(org, id, {
        category: cat, hardshipCode: cat === '1' ? driver : null,
      })
      // The suppression is the consequence that matters — it is what stops a send being triggered
      // at all. Reporting it back is how an officer knows the protection actually took effect.
      setMsg(r.suppressionApplied
        ? `Saved. Sends suppressed (${TIER_LABEL[r.suppressionApplied.tier]}`
          + `${r.suppressionApplied.until ? `, until ${dateOf(r.suppressionApplied.until)}` : ', indefinite'}).`
        : 'Saved. No suppression applies to this category.')
      load()
    } catch (e) {
      setMsg(e instanceof ApiError ? `Not saved: ${e.detail}` : String(e))
    } finally { setBusy(false) }
  }

  if (error) return <div className="page-state page-state-error">Could not load: {error}</div>
  if (!d) return <div className="page-state">Loading…</div>

  const c = d.case
  const drifted = d.live.balanceCents !== c.balanceCents

  return (
    <div className="ams">
      <header className="ams-head">
        <div>
          <h2>{c.residentName} <span className="muted">{c.accountNo}</span></h2>
          <p className="muted">{c.address} · Division {c.division}</p>
        </div>
        {/* Only what this role can actually do. A greyed row of four buttons tells a call centre
            agent what they are missing; showing the one they hold tells them what their job is.
            Every one of these is a link to the screen that does the work — no action fires from
            here, because a one-click "Escalate" with no note is how a case moves with no reason
            attached to it. */}
        <div className="actions">
          <Link className="btn" to={`/ams/inquiry?case=${c.id}`}>Every touchpoint</Link>
          {can(identity, 'arrears.visit') && (
            <Link className="btn" to="/ams/visits">House visits</Link>
          )}
          {can(identity, 'arrears.approvals.view') && (
            <Link className="btn" to="/ams/approvals">Approvals</Link>
          )}
        </div>
      </header>

      {d.rule && <RuleBanner rule={d.rule} />}

      {/* The suppression in force, with who applied it and until when. This is the record that
          answers "why did this resident not receive a letter", which is the question an auditor
          and a complaining resident both ask. */}
      {d.suppression && (
        <div className="notice ok">
          <b>Sends are suppressed on this case.</b>{' '}
          {d.suppression.feeBearingOnly
            ? 'Fee-bearing and legal stages only — statements continue.'
            : 'No sends at all.'}{' '}
          {d.suppression.suppressUntil
            ? `Until ${dateOf(d.suppression.suppressUntil)}.`
            : 'Indefinitely.'}
          <div className="muted sm">
            Applied by {d.suppression.setBy} on {dateOf(d.suppression.setAt)}
            {d.suppression.reason ? ` · ${d.suppression.reason}` : ''}
          </div>
        </div>
      )}

      <div className="grid-21">
        <div>
          {/* Recording a conversation sits ABOVE the history, because the person who needs it is
              on the phone right now and the history is what they read while listening. Gated on
              arrears.contact.log, which a call centre agent has and which does not carry the power
              to classify, suppress or send anything. */}
          {can(identity, 'arrears.contact.log') && (
            <RecordContact org={org!} caseId={id} resident={d.case.residentName} onSaved={load} />
          )}

          <Touchpoints events={d.events} />

          <section className="card">
            <h3>Ledger — from TCMS</h3>
            <table className="tbl">
              <thead><tr><th>Posted</th><th>Type</th><th>Description</th><th>Amount</th></tr></thead>
              <tbody>
                {d.ledger.map((l, i) => (
                  <tr key={i}>
                    <td>{dateOf(l.postedOn)}</td>
                    <td className="muted">{l.kind}</td>
                    <td>{l.description}</td>
                    <td className={l.amountCents < 0 ? 'pos' : ''}>{money(l.amountCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>

        <div>
          <section className="card">
            <h3>Account</h3>
            {/* The live figure, its age, and the cached one if they differ. Drift is shown, not
                smoothed over: it usually means a payment landed since the queue was built. */}
            <dl className="kv">
              <dt>Owed (TCMS, live)</dt><dd>{money(d.live.balanceCents)}</dd>
              <dt>Read</dt><dd className="muted">{ageOf(d.live.asOf)}</dd>
              <dt>Days overdue</dt><dd>{d.live.daysOverdue}</dd>
              <dt>Months of charge</dt>
              <dd className="muted">{d.live.monthsOfCharge.toFixed(1)} <span className="xs">MND basis only</span></dd>
              <dt>Category</dt>
              <dd>{CATEGORIES[c.category ?? 'unsegmented']}{c.hardshipCode ? ` · ${c.hardshipCode}` : ''}</dd>
              <dt>Priority</dt>
              <dd>{c.priorityScore} <span className="muted xs">({c.priorityBasis?.basis})</span></dd>
              <dt>Assigned</dt><dd>{ROLE_LABELS[c.assignedRole ?? ''] ?? '—'}</dd>
              {c.evidenceExpiresOn && (
                <><dt>Evidence expires</dt><dd>{dateOf(c.evidenceExpiresOn)}</dd></>
              )}
            </dl>
            {drifted && (
              <p className="notice">
                The cached figure on the queue was {money(c.balanceCents)}. The live balance differs,
                so the queue was built before the account moved.
              </p>
            )}
          </section>

          {/* Hidden outright for a role that cannot segment, rather than shown with the button
              greyed. A disabled control says "you may do this later"; a call centre agent never
              may, and an editor they cannot use is an invitation to ask why not. Reading the
              case stays open to them — it is deciding about it that does not. */}
          {can(identity, 'arrears.case.manage') && (
          <section className="card">
            <h3>Segment this case</h3>
            <p className="muted sm">
              Category decides the treatment. Setting Category 1 records a suppression, and AMS
              then declines to trigger any send covered by the rule — so it is not a label.
            </p>
            <label className="f">Resident category</label>
            <select value={cat} onChange={e => setCat(e.target.value)}>
              <option value="">Not yet segmented</option>
              {['1', '2', '3', '4'].map(k => <option key={k} value={k}>{k} · {CATEGORIES[k]}</option>)}
            </select>
            {cat === '1' && (
              <>
                <label className="f">Hardship driver (required)</label>
                <select value={driver} onChange={e => setDriver(e.target.value)}>
                  <option value="">Choose a driver…</option>
                  {Object.entries(DRIVERS).map(([k, v]) => (
                    <option key={k} value={k}>{k} · {v}</option>
                  ))}
                </select>
              </>
            )}
            <button className="btn pri" style={{ marginTop: 12 }}
                    disabled={busy || !cat || (cat === '1' && !driver)
                              || !identity.role || !canManage(identity.role)}
                    onClick={save}>
              {busy ? 'Saving…' : 'Save category'}
            </button>
            {msg && <p className="notice ok" style={{ marginTop: 10 }}>{msg}</p>}
          </section>
          )}

          {d.approvals.length > 0 && (
            <section className="card">
              <h3>Letters and approvals</h3>
              <ul className="plain">
                {d.approvals.map(a => (
                  <li key={a.id}>
                    <b>{a.stageKey}</b> — {a.status}
                    {a.blockedReason && <div className="muted sm">{a.blockedReason}</div>}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </div>
    </div>
  )
}

function canManage(role: string): boolean {
  return ['arrears_manager', 'arrears_officer', 'clt_officer', 'tc_admin'].includes(role)
}

/** The governing rule, in full. A hard stop is styled as a stop, because it is one. */
function RuleBanner({ rule }: { rule: HardshipRule }) {
  return (
    <div className={`notice ${rule.tier === 'hard_stop' ? 'stop' : rule.blocksDemand ? '' : 'ok'}`}>
      <b>{rule.code} {rule.driver} · {TIER_LABEL[rule.tier]}</b>
      <p>{rule.treatment}</p>
      <p className="muted sm"><b>Offer:</b> {rule.offer}</p>
      <p className="muted sm"><b>Evidence:</b> {rule.evidence}</p>
      {rule.blocksDemand && (
        <p className="sm">
          <b>No demand letter may be raised on this case.</b> The server refuses it regardless of
          role, so this is a control and not a warning.
        </p>
      )}
    </div>
  )
}
